// Grades the registered win probabilities (wp-1) against final scores. Display-only: it reads the
// manifest (final scores) and the filed public forecasts, and never writes either.
//
// Two cohorts, never mixed (DEC-011, DEC-012 D2):
//   prospective   — weeks a registered model covered before kickoff (2026: Week 4 on), pregame editions only;
//   afterTheFact  — earlier weeks, with q calculated later from the original score forecasts.
// Ties are excluded from every metric and counted visibly.

import { isLateEdition, matchupOutcome } from './predictionScorecard';
import { isProspective, issuedPickProbability, pickProbability, probA, WP_MODELS, CURRENT_WP_MODEL_ID } from './winProbability';

export const COIN_FLIP_BRIER = 0.25;
export const BAND_Z_90 = 1.645;

// Expected vs actual wins, the descriptive 90% band Σq ± 1.645·√Σq(1−q) (assumes independent games;
// the display clamps it to 0..n), and Brier mean((q − y)²).
export function summarizeProbabilities(rows) {
  const n = rows.length;
  if (!n) return { n: 0, expectedWins: null, actualWins: 0, bandLow: null, bandHigh: null, brier: null };
  const expectedWins = rows.reduce((sum, row) => sum + row.q, 0);
  const actualWins = rows.reduce((sum, row) => sum + row.y, 0);
  const spread = BAND_Z_90 * Math.sqrt(rows.reduce((sum, row) => sum + row.q * (1 - row.q), 0));
  const brier = rows.reduce((sum, row) => sum + (row.q - row.y) ** 2, 0) / n;
  return {
    n,
    expectedWins,
    actualWins,
    bandLow: expectedWins - spread,
    bandHigh: expectedWins + spread,
    brier,
  };
}

// Season-average baseline for week k: each team's mean ACTUAL score over graded weeks before k
// (any edition; final scores are facts). Null when no earlier week is graded (always Week 1).
export function seasonAverages(issues, week) {
  const earlier = issues.filter((issue) => issue.week < week && issue.results?.scores);
  if (!earlier.length) return null;
  const totals = {};
  for (const issue of earlier) {
    for (const [teamId, score] of Object.entries(issue.results.scores)) {
      const value = Number(score);
      if (!Number.isFinite(value)) continue;
      totals[teamId] = totals[teamId] || { sum: 0, count: 0 };
      totals[teamId].sum += value;
      totals[teamId].count += 1;
    }
  }
  return Object.fromEntries(Object.entries(totals).map(([teamId, { sum, count }]) => [teamId, sum / count]));
}

// The baseline pick is the team with the higher season average; its q uses the same Φ(·/σ).
export function baselinePick(matchup, averages, sigma) {
  if (!averages || !Array.isArray(matchup?.teamIds) || matchup.teamIds.length !== 2) return null;
  const [a, b] = matchup.teamIds.map(String);
  const meanA = averages[a];
  const meanB = averages[b];
  if (!Number.isFinite(meanA) || !Number.isFinite(meanB)) return null;
  if (meanA === meanB) return { pickId: null, q: 0.5 };
  const pickId = meanA > meanB ? a : b;
  return { pickId, q: probA(pickId === a ? meanA : meanB, pickId === a ? meanB : meanA, sigma) };
}

function teamsMap(forecast) {
  return Object.fromEntries((forecast?.teams || []).map((team) => [String(team.id), team]));
}

// One row per filed matchup with its q, the outcome, and the baseline's pick for that week.
export function probabilityRows(issues, forecastsByWeek, sigma = WP_MODELS[CURRENT_WP_MODEL_ID].sigma) {
  const rows = [];
  for (const issue of issues.slice().sort((x, y) => x.week - y.week)) {
    const forecast = forecastsByWeek[issue.week];
    if (!forecast) continue;
    const teams = teamsMap(forecast);
    const averages = seasonAverages(issues, issue.week);
    for (const matchup of forecast.matchups || []) {
      const outcome = matchupOutcome(matchup, issue.results?.scores);
      const baseline = baselinePick(matchup, averages, sigma);
      rows.push({
        week: issue.week,
        matchupId: matchup.id,
        // The issued q (frozen from Week 5) when grading the registered model; recomputed for any other sigma.
        q: sigma === WP_MODELS[CURRENT_WP_MODEL_ID].sigma ? issuedPickProbability(matchup, teams) : pickProbability(matchup, teams, sigma),
        status: outcome ? outcome.status : 'pending',
        y: outcome && outcome.status !== 'tie' ? Number(outcome.status === 'correct') : null,
        baseline: baseline ? {
          pickId: baseline.pickId,
          q: baseline.q,
          y: outcome && outcome.status !== 'tie' && baseline.pickId ? Number(baseline.pickId === outcome.winnerId) : null,
        } : null,
      });
    }
  }
  return rows;
}

function summarizeCohort(weeks, rows, issuesByWeek) {
  const graded = rows.filter((row) => row.y !== null && Number.isFinite(row.q));
  const ties = rows.filter((row) => row.status === 'tie').length;
  const gradedWeeks = weeks.filter((week) => issuesByWeek[week]?.results?.scores);
  const withBaseline = graded.filter((row) => row.baseline && row.baseline.pickId);
  const noBaselineWeeks = gradedWeeks.filter((week) => rows.some((row) => row.week === week && !row.baseline));
  const baselineNoPick = graded.filter((row) => row.baseline && !row.baseline.pickId).length;
  const baselineSummary = summarizeProbabilities(withBaseline.map((row) => ({ q: row.baseline.q, y: row.baseline.y })));
  const aiOnBaselineGames = summarizeProbabilities(withBaseline.map(({ q, y }) => ({ q, y })));
  return {
    weeks,
    gradedWeeks,
    pendingWeeks: weeks.filter((week) => !gradedWeeks.includes(week)),
    ties,
    ai: summarizeProbabilities(graded.map(({ q, y }) => ({ q, y }))),
    coinFlip: { brier: COIN_FLIP_BRIER, expectedWins: graded.length / 2 },
    baseline: {
      weeks: [...new Set(withBaseline.map((row) => row.week))],
      noBaselineWeeks,
      noPick: baselineNoPick,
      n: baselineSummary.n,
      correct: baselineSummary.actualWins,
      brier: baselineSummary.brier,
      ai: { correct: aiOnBaselineGames.actualWins, brier: aiOnBaselineGames.brier },
    },
  };
}

// forecastsByWeek: { [week]: public forecast JSON } for every issue of the season.
export function buildProbabilityScorecard(issues, forecastsByWeek, season) {
  const seasonIssues = issues.filter((issue) => season == null || issue.season === season);
  const issuesByWeek = Object.fromEntries(seasonIssues.map((issue) => [issue.week, issue]));
  const rows = probabilityRows(seasonIssues, forecastsByWeek);
  const prospectiveWeeks = [];
  const lateExcludedWeeks = [];
  const afterTheFactWeeks = [];
  for (const issue of seasonIssues.slice().sort((x, y) => x.week - y.week)) {
    if (!isProspective(issue)) afterTheFactWeeks.push(issue.week);
    else if (isLateEdition(issue)) lateExcludedWeeks.push(issue.week);
    else prospectiveWeeks.push(issue.week);
  }
  const pick = (weeks) => rows.filter((row) => weeks.includes(row.week));
  const firstProspectiveWeek = season === WP_MODELS[CURRENT_WP_MODEL_ID].season
    ? WP_MODELS[CURRENT_WP_MODEL_ID].effectiveFromWeek
    : 1;
  return {
    season: season ?? null,
    modelId: CURRENT_WP_MODEL_ID,
    sigma: WP_MODELS[CURRENT_WP_MODEL_ID].sigma,
    registeredAt: WP_MODELS[CURRENT_WP_MODEL_ID].registeredAt,
    firstProspectiveWeek,
    missingWeeks: seasonIssues.filter((issue) => !forecastsByWeek[issue.week]).map((issue) => issue.week),
    prospective: { ...summarizeCohort(prospectiveWeeks, pick(prospectiveWeeks), issuesByWeek), lateExcludedWeeks },
    afterTheFact: summarizeCohort(afterTheFactWeeks, pick(afterTheFactWeeks), issuesByWeek),
  };
}
